import { ReactNode, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api, setTenantOverride, getActiveTenantCode } from '../../services/api';
import SiteHeader from './SiteHeader';
import { VERSION_STRING } from '../../version';

interface AdminLayoutProps {
  children: ReactNode;
}

// Icon helpers — inline SVG keeps the bundle light
const Icon = {
  docs: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
  users: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  apps: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  tenants: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
      <line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  ),
  companies: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  settings: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  email: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  preqal: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4"/>
      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
    </svg>
  ),
};

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;

  const [tenants, setTenants]           = useState<{ id: string; code: string; name: string }[]>([]);
  const [activeTenant, setActiveTenant] = useState<string>(getActiveTenantCode());
  const [isAiAdmin, setIsAiAdmin]       = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.superAdmin.listTenants()
      .then((data) => setTenants(data.tenants ?? []))
      .catch(() => {/* non-fatal */});
  }, [isSuperAdmin]);

  // Check AI admin access once on mount — hides the nav item for non-AI-admins
  useEffect(() => {
    api.aiConfig.get()
      .then(() => setIsAiAdmin(true))
      .catch(() => setIsAiAdmin(false));
  }, []);

  function handleTenantChange(code: string) {
    setActiveTenant(code);
    setTenantOverride(code);
  }

  const navItems = [
    { path: '/admin/companies',                      label: 'Cégek',                            icon: Icon.companies },
    { path: '/admin/users',                          label: 'Felhasználók',                     icon: Icon.users  },
    { path: '/admin/applications',                   label: 'Pályázók',                         icon: Icon.apps   },
    
    { path: '/admin/chamber-registry',               label: 'Kamarai nyilvántartás',            icon: Icon.settings },
    { path: '/admin/invites',                        label: 'Meghívók és napló',                icon: Icon.users  },
    { path: '/admin/email-log',                      label: 'E-mail napló',                     icon: Icon.email  },
    { path: '/admin/statistics',                     label: 'Statisztikai adatok letöltése',    icon: Icon.docs   },
    
    { path: '/admin/email-templates',                label: 'E-mail sablonok',                  icon: Icon.email  },
    { path: '/admin/section-config',                 label: 'Form szekció konfiguráció',        icon: Icon.settings },
    { path: '/admin/scoring-rules',                  label: 'Pontozási szabályok',               icon: Icon.settings },
{ path: '/admin/declaration-config',             label: 'Nyilatkozat konfiguráció',         icon: Icon.settings },
    { path: '/admin/prequalification/questions',     label: 'Előminősítési kérdések',           icon: Icon.preqal },
    { path: '/admin/documents',                       label: 'Dokumentumtár',                    icon: Icon.docs },
    { path: '/admin/deadlines',                        label: 'Határidők',                        icon: Icon.settings },
    { path: '/admin/help-content',                    label: 'Segítség tartalom',                icon: Icon.settings },
    // AI config only shown when the current user is in AI_ADMIN_USER_IDS (checked via API)
    ...(isAiAdmin ? [{ path: '/admin/ai-config', label: 'AI konfiguráció', icon: Icon.settings }] : []),
    // Tenants menu item intentionally hidden -- route disabled, files kept for future use
  ];

  const displayName = user?.email?.split('@')[0] ?? 'Admin';
  const initials    = displayName.slice(0, 1).toUpperCase();
  const roleLabel   = isSuperAdmin ? 'Super Admin' : 'Adminisztrátor';

  return (
    <div className="app-shell">
      <SiteHeader roleLabel={roleLabel} />

      <div className="app-body">
        <aside className="app-sidebar">
          {/* Role badge */}
          <div style={{ padding: '14px 16px 6px' }}>
            <span className="badge badge-primary" style={{ fontSize: 11, letterSpacing: '.06em' }}>
              {roleLabel.toUpperCase()}
            </span>
          </div>

          <nav style={{ padding: '8px 12px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {navItems.map((item) => (
              <button
                key={item.path}
                className={`nav-item ${location.pathname.startsWith(item.path) ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </nav>

          <div style={{ flex: 1 }} />

          {isSuperAdmin && tenants.length > 0 && (
            <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginBottom: 5, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>
                Aktív tenant
              </div>
              <select
                value={activeTenant}
                onChange={(e) => handleTenantChange(e.target.value)}
                style={{
                  width: '100%', padding: '6px 8px', fontSize: 12,
                  border: '1px solid var(--border)', borderRadius: 6,
                  background: 'var(--background)', color: 'var(--foreground)',
                  cursor: 'pointer',
                }}
              >
                {tenants.map((t) => (
                  <option key={t.code} value={t.code}>{t.name} ({t.code})</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ padding: '6px 16px 4px', textAlign: 'center' }}>
            <span style={{ fontSize: 10, color: 'var(--muted-foreground)', fontFamily: 'monospace', letterSpacing: '.04em' }}>
              {VERSION_STRING}
            </span>
          </div>

          <div style={{ padding: '10px 16px 14px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'var(--primary)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 12, flexShrink: 0,
            }}>{initials}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{displayName}</div>
              <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{roleLabel}</div>
            </div>
          </div>
        </aside>

        <main className="app-main">{children}</main>
      </div>
    </div>
  );
}
